import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {HttpClient} from '@angular/common/http';
import {classToClass, plainToClass} from 'class-transformer';
import {SelectItem, TreeNode} from 'primeng/api';
import {BehaviorSubject, Observable} from 'rxjs';
import {ProjectChargeCodeArray, ProjectChargeCodeDropdown, ProjectWorkLocationArray} from 'app/shared/classes/charge-code';

import {UserPhoto} from 'app/shared/classes/employee';
import {TreeView, UserLookupDetails} from 'app/shared/classes/user';

import {ApprovalStatusType} from 'app/shared/enums/approval-status-type.enum';
import {Browser} from 'app/shared/enums/browser.enum';
import {EnvironmentType} from 'app/shared/enums/environment-type.enum';
import {Roles} from 'app/shared/enums/roles.enum';
import {RouteLink} from 'app/shared/enums/route-link.enum';
import {WorklistType} from 'app/shared/enums/worklist-type.enum';

import {CommonDataService} from 'app/shared/services/common-data.service';
import {DateService} from 'app/shared/services/date.service';
import {DirectoryService} from 'app/shared/services/directory.service';
import {TreeViewId, TreeViewNodeId, TreeViewType, UserType} from "app/shared/enums/user-type.enum";

import {LocalCacheService} from "app/core/local-cache/local-cache.service";
import {GrowlService} from 'app/core/growl/growl.service';
import {LocalStorageService} from "@app/core/local-storage/local-storage.service";
import {ProjectWorkLocation} from "@app/shared/classes/project";
import {StatusCount} from "@app/shared/classes/status-count";
import {AbstractTodoTask} from "@app/shared/classes/abstract-todo-task";
import {KeycloakSupportService} from "@app/core/keycloak/keycloak-support.service";
import {KeycloakService} from "keycloak-angular";
import {UtilityService} from "@app/shared/services/utility.service";
import {TreeTable} from "primeng/treetable";
import {TreeDataTableComponent} from "@app/shared/table/tree-data-table/tree-data-table.component";

@Injectable({
  providedIn: 'root',
})
export class UserService {

  info: UserLookupDetails;                        // user's information
  photo: UserPhoto;                               // user's photo to be displayed on topbar
  chargeCodes: ProjectChargeCodeArray;            // user's authorized charge codes
  projectWorkLocations: ProjectWorkLocationArray; // Project work locations.
  resourceManagerSubordinates: SelectItem[];      // user's direct reports

  worklistSubmittedOrApproved: number = 0;        // set after retrieving user info and then whenever worklist component is loaded

  authenticated: boolean;                         // set after login/logout of keycloak
  dataserviceAvailable: boolean;                  // set after retrieving user info from the backend
  hasFullAccessToMenu: boolean;                   // set after retrieving user info from the backend

  instance: EnvironmentType;                      // set for different environment e.g. dev, qa, production
  routeToAfterAuthenticated: string;              // route after authentication

  managingOrgId: number;                          // used for solution manager if the user has the privilege
  managingOrgName: string;                        // used for solution manager if the user has the privilege
  managingRealm: string;                          // used for solution manager if the user has the privilege

  hasRole: { [key: string]: boolean } = {};

  browserType: Browser;

  enableConnect: boolean;
  enableSupport: boolean;

  static userInfoSub: BehaviorSubject<boolean> = new BehaviorSubject(false);
  static userInfoObs: Observable<boolean> = UserService.userInfoSub.asObservable();
  static userSubordinateSub: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private treeViewType: TreeViewType;
  private treeView: TreeView;

  constructor(private http: HttpClient,
              private router: Router,
              private keycloakAngular: KeycloakService,
              private keycloakSupport: KeycloakSupportService,
              private cache: LocalCacheService,
              private growl: GrowlService,
              private commonData: CommonDataService,
              private dateService: DateService,
              private dirService: DirectoryService,
              private localStorage: LocalStorageService,
              private utilityService: UtilityService) {

    this.checkBrowserType();
  }

  /**
   * @desc determine which browser is used by user
   * @returns this.browserType - find and set browser user is using
   */
  checkBrowserType(): void {
    const userAgent = window.navigator.userAgent;
    const browsers = {Edge: /edge/i, Chrome: /chrome/i, Safari: /safari/i, Firefox: /firefox/i, Ie: /internet explorer/i};
    if (userAgent.indexOf("edge") > -1) {
      this.browserType = Browser.Edge;
      return;
    }
    for (const key in browsers) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
      if (browsers[key].test(userAgent)) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.browserType = Browser[key];
        return;
      }
    }
  }

  resetUser(): void {
    this.cache.reset();
    this.deleteTreeViewCache();
    this.authenticated = false;
    this.info = null;
    this.photo = null;
    this.chargeCodes = null;
    this.projectWorkLocations = null;
    this.resourceManagerSubordinates = null;
    this.worklistSubmittedOrApproved = 0;

    this.dataserviceAvailable = false;
    this.routeToAfterAuthenticated = null;

    this.managingOrgId = null;
    this.managingOrgName = null;
    this.managingRealm = null;

    this.hasRole = {};

    this.browserType = null;
  }

  resetAndRouteToLogin(): void {
    this.resetUser();
    UserService.userInfoSub.next(false);
    UserService.userSubordinateSub.next(false);
    void this.keycloakSupport.logout().then();
  }

  /**
   * @desc clear all tokens, handlers, and properties to null/false. route to the login page
   * @returns reset all class variables
   */
  logout(): void {
    this.enableConnect = false;
    this.enableSupport = false;

    if (this.authenticated) {
      this.commonData.setLastLogForSelf('LOGOUT').subscribe(res => {
        this.resetAndRouteToLogin();
      }, err => {
        this.resetAndRouteToLogin();
      });
    } else {
      this.resetAndRouteToLogin();
    }
  }

  /**
   * @desc check whether user is authenticated and has necessary role in his token
   */
  isAuthorized(roles: Array<Roles>): boolean {

    let authorized = false;

    if (this.authenticated !== undefined) {
      authorized = this.authenticated;
    }

    if (authorized) {
      // check whether user's keycloak token contains a role specified in the input roles
      authorized = false;
      for (const role of roles) {
        if (this.keycloakAngular.isUserInRole(role)) {
          authorized = true;
          break;
        }
      }
    } else {
      console.warn(`isAuthorized: User does not have the necessary role(s):`);
    }

    return authorized;
  }

  onAuthSuccess(): void {
    this.enableConnect = true;
    this.enableSupport = true;
    this.authenticated = true;
    this.keycloakSupport.saveKeycloakOptions(this.keycloakAngular.getKeycloakInstance());
    void this.keycloakAngular.loadUserProfile(); // ToDo jC: this may not be needed...
    this.setAuthorizedRoles();

    console.log("invoking processAfterAuth() from onAuthSuccess()...");
    this.processAfterAuth();
  }

  /**
   *
   */
  routeAfterAuth(): void {

    // if the user is a candidate, route the user to the candidate home page
    if (!this.routedIfCandidateOrCustomer()) {
      // user is  employee, consultant, contractor or new, route to the specified component/page in the url
      if (this.routeToAfterAuthenticated && this.routeToAfterAuthenticated !== RouteLink.CheckAuth && this.routeToAfterAuthenticated != '/') {
        console.log(`processAfterAuth: routing to ${this.routeToAfterAuthenticated}`);
        void this.router.navigate([this.routeToAfterAuthenticated]);
      } else {
        console.log(`processAfterAuth: default routing to [${RouteLink.Home}]`);
        void this.router.navigate([RouteLink.Home]);
      }
    }
  }

  /**
   * @desc Set user context by retrieving user's info including id, name, picture, worklist, direct reports, ... etc.
   *        Then,route to the specified url after login
   */
  processAfterAuth(): void {
    this.chargeCodes = null;
    this.projectWorkLocations = null;

    this.loadTreeViewsFromLocalStorage();

    if (!this.info) {
      this.commonData.getUser().subscribe(res => {
          this.info = plainToClass(UserLookupDetails, res);
          this.dataserviceAvailable = true;

          // set managing org info
          this.managingOrgId = this.info.orgId;
          this.managingOrgName = this.info.orgName;
          this.managingRealm = this.info.realm;

          // user info is now available, let the subscribers know
          UserService.userInfoSub.next(true);
          this.showMyPicture();

          if (this.info.subtype != UserType.Candidate && this.info.subtype != UserType.Customer) {
            this.getWorklistCount();
            this.getResourceManagerSubordinates();
            this.hasFullAccessToMenu = true;
          } else {
            this.hasFullAccessToMenu = false;
          }
          this.routeAfterAuth();
        },
        error => {
          console.error("processAfterAuth: Could not retrieve user info. Route to the error page.");
          this.dataserviceAvailable = false;
          void this.router.navigate([RouteLink.ServiceUnavailable]);
        });
    } else {
      this.routeAfterAuth();
    }
  }


  /**
   * @desc If the user is a candidate, route the user to the candidate home page
   *       The caller method should invoke "return" if this method returns true
   */
  routedIfCandidateOrCustomer(): boolean {
    let routed = false;

    if (this.info?.subtype == UserType.Candidate) {
      void this.router.navigate([RouteLink.CandidateHome]);
      routed = true;
    } else if (this.info?.subtype == UserType.Customer) {
      void this.router.navigate([RouteLink.CustomerHome]);
      routed = true;
    }

    return routed;
  }

  /**
   * @desc show user's picture on the topbar
   */
  showMyPicture(): void {
    if (!this.photo) {
      this.commonData.getPhotoByUserId(this.info.userId).subscribe(res => {
        this.photo = plainToClass(UserPhoto, res);
        let myPicture = document.getElementById('myPicture') as HTMLImageElement;
        if (myPicture) {
          if (this.photo && this.photo.data) {
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            myPicture.src = this.utilityService.buildHtmlImageSrc(this.photo.contentType, this.photo.data);
          } else {
            myPicture.src = "assets/img/profile-back.png";
          }
        }
      });
    }
  }

  /**
   * @desc check if the input userId is one user's direct report
   * @returns true if the input userId is one of user's direct report
   */
  isDirectReport(userId: number): boolean {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return (this.resourceManagerSubordinates && this.resourceManagerSubordinates.findIndex(user => user.value.id == userId) >= 0);
  }

  saveProjectWorkLocations(index: string, projectWorkLocations: ProjectWorkLocation[]) {
    if (!this.projectWorkLocations) {
      this.projectWorkLocations = new ProjectWorkLocationArray();
    }
    if (index && !this.projectWorkLocations[index]) {
      this.projectWorkLocations[index] = classToClass(projectWorkLocations);
    } else if (index && this.projectWorkLocations[index]) {
      console.warn("the input Project Work locations already exists. not saving it...");
    }
  }

  /**
   * @desc save authorized charge codes for the user, so that it can be available during the session without calling the backend again.
   */
  saveAuthorizedChargeCodeArray(index: string, chargeCodeArray: ProjectChargeCodeDropdown[]): void {

    if (!this.chargeCodes) {
      this.chargeCodes = new ProjectChargeCodeArray();
    }
    if (index && !this.chargeCodes[index]) {
      this.chargeCodes[index] = classToClass(chargeCodeArray);
    } else if (index && this.chargeCodes[index]) {
      console.warn("the input charge code already exists. not saving it...");
    }
  }

  /**
   * @desc remove authorized charge codes for the user, thus not available during the session. this method should not be called...
   */
  removeAuthorizedChargeCodeArray(index: string): void {
    if (this.chargeCodes && this.chargeCodes[index]) {
      this.chargeCodes[index] = null;
    }
  }

  /**
   * @desc get a copy of the authorized charge codes for the user saved in the cache - this.chargeCodes
   *       a copy of returned so that it can not be altered outside of saveAuthorizedChargeCodeArray()
   */
  getAuthorizedChargeCodeArray(index: string): ProjectChargeCodeDropdown[] {
    if (this.chargeCodes && this.chargeCodes[index]) {
      return classToClass(this.chargeCodes[index]);
    } else {
      return null;
    }
  }

  getProjectWorkLocation(index: string) {
    if (this.projectWorkLocations && this.projectWorkLocations[index]) {
      return classToClass(this.projectWorkLocations[index]);
    } else {
      return null;
    }
  }

  /**
   * initialize tree view for a specific component; this method must be invoked before using expand/collapse operations
   * other than Project, treeId are determined based on treeViewType
   * treeId in project represents a project id
   */
  initializeTreeView(treeViewType: TreeViewType, treeId?: number, treeViewNodeId?: string): void {
    this.treeViewType = treeViewType;
    if (treeId == null) {
      treeId = this.getTreeId(treeViewType);
    }
    if (this.cache.treeViews[treeViewType]) {
      this.treeView = <TreeView>(this.cache.treeViews[treeViewType].find((view: TreeView) => view.treeId == treeId));
    } else {
      this.cache.treeViews[treeViewType] = [];
    }
    if (this.treeView == null) {
      this.treeView = new TreeView(treeId, treeViewNodeId);
      this.cache.treeViews[treeViewType].push(this.treeView);
    }
  }

  getExpandedTreeNodeIds(): Set<number | string> {
    return this.treeView ? new Set(this.treeView.expandedNodeIds) : new Set();
  }

  expandOrCollapseTreeNodesFromCache(treeNodes: TreeNode[], treeDataTable?: TreeDataTableComponent) {
    if (this.treeViewType && treeNodes) {
      const numExpanded = this.setExpandedTreeNodes(treeNodes, 0);
      console.error(`# expanded = ${numExpanded}`);
    }
    let sizeOfTreeNodes: number;
    sizeOfTreeNodes = this.getSizeOfTreeNodes(treeNodes);

    if (treeDataTable) {
      sizeOfTreeNodes = this.getSizeOfTreeNodes(treeNodes);
      console.log("# expanded = ", this.treeView.expandedNodeIds.size);
      console.log("# nodes = ", sizeOfTreeNodes);

      if (this.treeView.expandedNodeIds.size == sizeOfTreeNodes) {
        treeDataTable.allRowsExpanded = true;
      }
      treeDataTable.onHandleReloadValue();
    }
  }

  expandTreeTableAndSaveToCache(treeTable: TreeTable): void {
    this.expandTreeNodesAndSaveToCache(treeTable.value);
  }

  collapseTreeTableAndSaveToCache(treeTable: TreeTable) {
    this.collapseTreeNodesAndSaveToCache(treeTable.value);
  }

  expandTreeDataTableAndSaveToCache(treeDataTable: TreeDataTableComponent): void {
    this.expandTreeNodesAndSaveToCache(treeDataTable.value);
  }

  collapseTreeDataTableAndSaveToCache(treeDataTable: TreeDataTableComponent) {
    this.collapseTreeNodesAndSaveToCache(treeDataTable.value);
  }

  expandTreeNodesAndSaveToCache(treeNodes: TreeNode[]): void {
    if (treeNodes && treeNodes.length > 0) {
      this.expandTreeNodesAndSave(treeNodes);
      this.localStorage.add(this.treeViewType, this.cache.treeViews[this.treeViewType]);
    } else {
      console.warn(`cannot expand node(s) because treeNodes is empty.`);
    }
  }

  collapseTreeNodesAndSaveToCache(treeNodes: TreeNode[]): void {
    if (treeNodes && treeNodes.length > 0) {
      this.collapseTreeNodes(treeNodes);
      this.treeView.expandedNodeIds.clear();
      this.localStorage.add(this.treeViewType, this.cache.treeViews[this.treeViewType]);
    } else {
      console.warn(`cannot collapse node(s) because treeNodes is empty.`);
    }
  }

  saveExpandedTreeNodeIdToCache(nodeId: number | string): void {
    if (nodeId !== null) {
      this.treeView.expandedNodeIds.add(nodeId);
      this.localStorage.add(this.treeViewType, this.cache.treeViews[this.treeViewType]);
    } else {
      console.warn(`cannot add the nodeId (${nodeId}) because nodeId is null.`);
    }
  }

  saveCollapsedTreeNodeIdToCache(nodeId: number | string): void {
    if (nodeId != null) {
      this.treeView.expandedNodeIds.delete(nodeId);
      this.localStorage.add(this.treeViewType, this.cache.treeViews[this.treeViewType]);
    } else {
      console.warn(`cannot add the nodeId (${nodeId}) because nodeId is null.`);
    }
  }

  saveExpandedTreeNodeToCache(node: TreeNode): void {
    if (node && node.data) {
      this.saveExpandedTreeNodeIdToCache(node.data[this.treeView.nodeId]);
    } else {
      console.warn(`cannot add null nodeId.`);
    }
  }

  saveCollapsedTreeNodeToCache(node: TreeNode): void {
    if (node && node.data) {
      this.saveCollapsedTreeNodeIdToCache(node.data[this.treeView.nodeId]);
    } else {
      console.warn(`cannot save null nodeId.`);
    }
  }

  private setAuthorizedRoles(): void {
    let authorizedRoles = this.keycloakAngular.getUserRoles(true);
    authorizedRoles.forEach(role => {
      this.hasRole[role] = true;
    });
  }

  /**
   * @desc get user's direct reports
   */
  private getResourceManagerSubordinates(): void {
    this.dirService.getResourceManagerSubordinatesAsDropdown().subscribe(res => {
      this.resourceManagerSubordinates = res;
      UserService.userSubordinateSub.next(true);
    });
  }

  /**
   * @desc get user's worklist
   */
  private getWorklistCount(): void {

    this.commonData.getWorklistCount(WorklistType.User).subscribe(res => {
      const statusCounts = plainToClass(StatusCount, res.list);
      this.worklistSubmittedOrApproved = 0;
      for (const status of statusCounts) {
        if (status.statusName === ApprovalStatusType.Submitted || status.statusName === ApprovalStatusType.Approved) {
          this.worklistSubmittedOrApproved += status.count;
        }

        this.commonData.getMyTasks().subscribe(res => {
          const myToDoTasks = plainToClass(AbstractTodoTask, res.list);
          const pendingTasks = myToDoTasks.filter(toDoTask => !toDoTask.completed).length;
          this.worklistSubmittedOrApproved += pendingTasks;
        });
      }
    });
  }

  /**
   * load tree views from the local storage and load it to cache
   * if user is logged in using tokens stored in the local storage, then tree views may exist
   * if user is logged in using passwd, then the local storage was deleted before the authentication, thus no tree views will be loaded
   */
  private loadTreeViewsFromLocalStorage(): void {
    for (const treeViewType of Object.values(TreeViewType)) {
      let treeViewFromStorage = this.localStorage.get(treeViewType);
      if (treeViewFromStorage) {
        this.cache.treeViews[treeViewType] = plainToClass(TreeView, treeViewFromStorage);
        this.cache.treeViews[treeViewType] = plainToClass(TreeView, this.localStorage.get(treeViewType));
        if (this.cache.treeViews[treeViewType] && this.cache.treeViews[treeViewType].length > 0) {
          for (let treeView of this.cache.treeViews[treeViewType]) {
            treeView.expandedNodeIds = new Set(treeView.expandedNodeIds);
          }
        }
      }
    }
  }

  private getTreeId(treeViewType: TreeViewType): number {
    let treeId: number;
    switch (treeViewType) {
      case TreeViewType.ChartOfAccount:
        treeId = TreeViewId.ChartOfAccount;
        break;
      case TreeViewType.GeneralLedger:
        treeId = TreeViewId.GeneralLedger;
        break;
      case TreeViewType.ItemCatalog:
        treeId = TreeViewId.ItemCatalog;
        break;
      case TreeViewType.ItemCatalogSummary:
        treeId = TreeViewId.ItemCatalogSummary;
        break;
      case TreeViewType.TrialBalance:
        treeId = TreeViewId.TrialBalance;
        break;
    }
    return treeId;
  }

  sizeOfTreeNoes: number;

  private getSizeOfTreeNodes(treeNodes: TreeNode[]): number {
    this.sizeOfTreeNoes = 0;
    this.traverseTreeNodes(treeNodes);
    return this.sizeOfTreeNoes;
  }

  private traverseTreeNodes(treeNodes: TreeNode[]): void {
    for (const treeNode of treeNodes) {
      this.sizeOfTreeNoes++;
      if (treeNode.children && treeNode.children.length > 0) {
        this.traverseTreeNodes(treeNode.children);
      }
    }
  }

  private setExpandedTreeNodes(treeNodes: TreeNode[], expanded: number): number {
    for (const treeNode of treeNodes) {
      const nodeId = treeNode.data[this.treeView.nodeId];
      if (nodeId && this.treeView.expandedNodeIds.has(nodeId)) {
        treeNode.expanded = true;
        expanded++;
      }
      if (treeNode.children && treeNode.children.length > 0) {
        this.setExpandedTreeNodes(treeNode.children, expanded);
      }
    }
    return expanded;
  }

  private expandTreeNodesAndSave(treeNodes: TreeNode[]): void {
    treeNodes.forEach(treeNode => {
      treeNode.expanded = true;
      const nodeId = treeNode.data[this.treeView.nodeId];
      this.treeView.expandedNodeIds.add(nodeId);
      if (treeNode.children && treeNode.children.length > 0) {
        this.expandTreeNodesAndSave(treeNode.children);
      }
    });
  }

  private collapseTreeNodes(treeNodes: TreeNode[]): void {
    treeNodes.forEach(treeNode => {
      treeNode.expanded = false;
      if (treeNode.children && treeNode.children.length > 0) {
        this.collapseTreeNodes(treeNode.children);
      }
    });
  }

  private deleteTreeViewCache(): void {
    for (const treeViewType of Object.values(TreeViewType)) {
      this.localStorage.delete(treeViewType);
    }
    this.cache.treeViews = [];
  }
}
